"use client";

// StatusCorner — the bottom-right info/clipboard readout. A frosted blur pill that
// (1) tells you in plain language what you're hovering (any element with a
// `data-hint`), and (2) drives the copy/paste UX: ⌘C arms a folder/file (the
// shell dispatches `innernet:clip`), the corner then offers "⌘V to paste · name",
// and ⌘V confirms the paste. Transient messages win over the hover hint.

import { useEffect, useRef, useState } from "react";
import { hintFor, composeHint } from "../../lib/os/hints";
import { NARRATE_EVENT, currentNarration, narrateAct, type NarrateDetail } from "../../lib/os/noticer";
import { Chevron } from "../ui";

// an act whose button leads forward (carries the chevron + full ink).
const isPrimaryAct = (act: string) =>
  act === "next" || act === "create" || act === "import" ||
  act.startsWith("goto:") || act.startsWith("unlock:");

export default function StatusCorner({ muteNarration = false }: { muteNarration?: boolean } = {}) {
  const [hint, setHint] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [armed, setArmed] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  // narration (the onboarding conductor speaking through the corner) outranks
  // everything and is interactive — the corner becomes clickable while it's set.
  // While the NettiGuide is mounted IT is the speaking surface (the conductor's
  // beats render there as chat), so the corner stays on plain hint duty.
  const [narrationRaw, setNarration] = useState<NarrateDetail | null>(null);
  const narration = muteNarration ? null : narrationRaw;
  const armedRef = useRef<string | null>(null);
  armedRef.current = armed;
  const cornerRef = useRef<HTMLDivElement>(null);
  // frozen while the cursor sits near the corner — so you can travel TO the corner
  // to read the full notice without the hover-tracking swapping it out underneath you.
  const nearCorner = useRef(false);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const showFlash = (t: string) => {
      setFlash(t);
      if (flashTimer.current) clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlash(null), 2600);
    };
    // Resolve the hovered element through a fallback chain, so the Noticer
    // explains EVERY surface: an explicit hint → a registry key → a directory
    // node's name → any control's aria-label/title. Opt-in is for quality;
    // coverage is total.
    const resolve = (target: HTMLElement | null): string | null => {
      if (!target) return null;
      const hintEl = target.closest?.("[data-hint]") as HTMLElement | null;
      if (hintEl) return hintEl.getAttribute("data-hint");
      const keyEl = target.closest?.("[data-hint-key]") as HTMLElement | null;
      if (keyEl) {
        const reg = hintFor(keyEl.getAttribute("data-hint-key") || "");
        if (reg) return composeHint(reg);
      }
      const nodeEl = target.closest?.("[data-node-key]") as HTMLElement | null;
      if (nodeEl) {
        const n = nodeEl.getAttribute("data-node-name");
        if (n) return `${n} — part of your memory`;
      }
      const ctrl = target.closest?.("button, a[href], [role='button'], input, select, [aria-label], [title]") as HTMLElement | null;
      // a region's name (the stage's scroller is the page's content, named for
      // screen readers) is not a control: the corner has nothing to say about it
      if (ctrl && !ctrl.matches("[data-stage-scroll], [role='region']")) {
        const lbl = ctrl.getAttribute("aria-label") || ctrl.getAttribute("title");
        if (lbl) return lbl;
      }
      return null;
    };
    const onOver = (e: PointerEvent) => {
      if (nearCorner.current) return; // hold the current notice while reading in the corner
      setHint(resolve(e.target as HTMLElement | null));
    };
    // proximity to the corner → unfurl the full text (and freeze the current notice)
    // once per FRAME, not per pointer event: this runs for every move over the
    // whole document, and a layout read on each one (getBoundingClientRect)
    // forced a synchronous relayout on every mouse move while anything was
    // animating. State changes only when the answer does.
    let proxRaf = 0;
    let lastX = 0;
    let lastY = 0;
    const onProx = (e: PointerEvent) => {
      lastX = e.clientX;
      lastY = e.clientY;
      if (proxRaf) return;
      proxRaf = requestAnimationFrame(() => {
        proxRaf = 0;
        const el = cornerRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const near = Math.hypot(lastX - r.right, lastY - r.bottom) < 210;
        if (near === nearCorner.current) return;
        nearCorner.current = near;
        setExpanded(near);
      });
    };
    const onClip = (e: Event) => {
      const d = (e as CustomEvent).detail as { action: string; name?: string; folder?: string };
      // there is no COPY in here — picking something up (⌘C / drag) MOVES it.
      if (d.action === "cut" && d.name) {
        setArmed(d.name);
        showFlash(`holding ${d.name} — ⌘V to move it into a folder`);
      } else if (d.action === "moved") {
        showFlash(`moved ${d.name ?? armedRef.current ?? "it"}${d.folder ? ` → ${d.folder}` : ""}`);
        setArmed(null);
      } else if (d.action === "note" && d.name) {
        showFlash(d.name);
      } else if (d.action === "clear") {
        setArmed(null);
      }
    };
    const onNarrate = (e: Event) => {
      const d = (e as CustomEvent).detail as NarrateDetail | null;
      // a clear of an aside hands the corner back to the line still speaking, if one is
      setNarration(d ?? currentNarration());
    };
    document.addEventListener("pointerover", onOver, { passive: true });
    document.addEventListener("pointermove", onProx, { passive: true });
    window.addEventListener("innernet:clip", onClip as EventListener);
    window.addEventListener(NARRATE_EVENT, onNarrate as EventListener);
    return () => {
      document.removeEventListener("pointerover", onOver);
      document.removeEventListener("pointermove", onProx);
      if (proxRaf) cancelAnimationFrame(proxRaf);
      window.removeEventListener("innernet:clip", onClip as EventListener);
      window.removeEventListener(NARRATE_EVENT, onNarrate as EventListener);
      if (flashTimer.current) clearTimeout(flashTimer.current);
    };
  }, []);

  // narration (conductor) outranks all; else flash (transient); else the move
  // affordance while holding something; else the hover hint.
  const narrating = narration != null;
  const raw = narration?.text ?? flash ?? (armed ? `⌘V to move ${armed} into this folder` : hint);
  // while the Noticer is speaking, the "ask netti" corner steps aside WHOLE —
  // mark and all (law_netti_one_voice). Stamped on :root so the corner button
  // reads it from CSS; it used to retire only the label, which left the mark
  // punctuating the end of every notice as if the two were one sentence.
  const live = raw != null || narrating;
  useEffect(() => {
    document.documentElement.toggleAttribute("data-noticer-live", live);
    return () => { document.documentElement.removeAttribute("data-noticer-live"); };
  }, [live]);
  // narration always shows in full; hints are summary-first (line 1 at rest).
  const display = raw == null ? null : narrating || expanded ? raw : raw.split("\n")[0];
  const show = raw != null || narrating;
  return (
    <>
      {/* the Noticer's own corner blur — seats the readout above scrolling content,
          widening as the notice unfurls (so the veil fits the text it's behind). */}
      {/* the veil recedes on the SAME ramp as the words it sits behind, so the
          frost never lingers in the corner after the notice has gone and the
          conversation never surfaces through a rectangle of someone else's blur.
          Presence is CSS's (`[data-show]` × `--noticer-on`) — an inline opacity
          here would have to re-declare the whole two-factor rule, and an inline
          transition on it would restart every frame the dock writes `--np`. */}
      <div
        className="os-corner-veil"
        data-corner="br"
        data-show={show ? "true" : undefined}
        aria-hidden
        style={{
          ["--veil-w" as string]: narrating ? "min(64vw, 520px)" : expanded ? "min(56vw, 460px)" : "min(40vw, 300px)",
          ["--veil-h" as string]: narrating ? "168px" : "112px",
        } as React.CSSProperties}
      >
        <i /><i /><i />
      </div>
      <div
        ref={cornerRef}
        className="os-status-corner"
        data-show={show ? "true" : undefined}
        data-expanded={(narrating || (expanded && raw)) ? "true" : undefined}
        data-narrating={narrating ? "true" : undefined}
        aria-live="polite"
      >
        {/* re-keyed per narration id so each new line shimmers in (law_sequential_unfold) */}
        <span key={narration?.id ?? "hint"} className={`os-status-text${narrating ? " os-status-text--shimmer" : ""}`}>{display}</span>
        {narrating && narration?.actions && narration.actions.length > 0 && (
          <span className="os-status-acts">
            {/* law_chevrons: the forward action ALWAYS sits rightmost (fixed spot),
                quiet actions before it; chevrons only, never stem arrows. */}
            {[...narration.actions].sort((x, y) => Number(isPrimaryAct(x.act)) - Number(isPrimaryAct(y.act))).map((a) => (
              <button key={a.act} type="button" className="os-status-act" data-primary={isPrimaryAct(a.act) || undefined} onClick={() => narrateAct(a.act)}>
                {a.label}
                {isPrimaryAct(a.act) && <Chevron size={12} className="os-status-chev" />}
              </button>
            ))}
          </span>
        )}
      </div>
    </>
  );
}
